import { CHECKOUT_ITEM_TYPE_BEDBANK, CHECKOUT_ITEM_TYPE_LE_HOTEL } from 'constants/checkout'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'

type PropertyRateUpgradeItem = App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem

/**
 * Toggle a property-rate package upgrade by re-shaping the item to the other side — synchronously, from
 * metadata captured at build time (eager dual-session; no network call). Applied → the target rate (books
 * target, charges source); removed → the source rate with `loyaltyAppliedUpgradeType` cleared but
 * `loyaltyPropertyRateUpgrade` kept so it can re-apply. `itemId`/`transactionKey`/occupancy/dates are
 * preserved; cross-service toggles flip `itemType` (`hotel` ↔ `bedbank`). No-op without an upgrade.
 */
function buildToggledPropertyRateItem(item: PropertyRateUpgradeItem): PropertyRateUpgradeItem {
  const upgrade = item.loyaltyPropertyRateUpgrade
  if (!upgrade) return item

  // Currently applied (undefined ⇒ applied) → remove; currently removed → re-apply.
  const nextApplied = upgrade.applied === false
  const ref = nextApplied ? upgrade.target : upgrade.source
  const booking = nextApplied ? upgrade.targetBooking : upgrade.sourceBooking
  const nextUpgrade: App.Checkout.PropertyRateLoyaltyUpgrade = { ...upgrade, applied: nextApplied }

  const common = {
    itemId: item.itemId,
    transactionKey: item.transactionKey,
    occupancy: item.occupancy,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    duration: item.duration,
    // These point at the base/source rate and only make sense while an upgrade is applied.
    loyaltyAppliedUpgradeType: nextApplied ? upgrade.appliedUpgradeType : undefined,
    loyaltyBasePackageId: nextApplied ? upgrade.source.servicePackageId : undefined,
    loyaltyBaseRateId: nextApplied ? upgrade.source.serviceRateId : undefined,
    loyaltyPropertyRateUpgrade: nextUpgrade,
    propertyRateRef: ref,
    ...(ref.bundleId && { isAccomBundle: ref.bundleId }),
    ...(item.bundledItemIds && { bundledItemIds: item.bundledItemIds }),
  }

  if (ref.serviceType === 'bedbank') {
    const bedbankItem: App.Checkout.BedbankHotelItem = {
      ...common,
      offerId: booking!.offerId,
      sessionId: booking?.sessionId,
      itemType: CHECKOUT_ITEM_TYPE_BEDBANK,
      // Bedbank items key off the bedbank *service* roomId — used to match `bedbankOfferRates` at checkout.
      roomId: booking!.roomId!,
      roomRateId: ref.serviceRateId,
      rateGroupId: booking?.rateGroupId,
      bedGroupId: booking?.bedGroupId ?? '',
      isFlightBundle: booking?.isFlightBundle ?? false,
      boardCode: booking?.boardCode,
      refundable: booking?.refundable ?? false,
    }
    return bedbankItem
  }

  const leItem: App.Checkout.InstantBookingLEHotelItem = {
    ...common,
    offerId: booking?.offerId ?? '',
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId: ref.servicePackageId ?? '',
    roomRateId: ref.serviceRateId,
    paymentScheduleInfo: booking?.paymentScheduleInfo,
  }
  return leItem
}

export default buildToggledPropertyRateItem
